/**
 * Reading inline content off the syntax tree (decisions 41, 66): text as the writer sees it, piece by piece,
 * each piece with the source span it comes from, plus the inline constructs around it. The model
 * (model.ts), the canonical form (serialize.ts) and the visible text (visible.ts) all read it from here.
 *
 * Whitespace follows CommonMark: a line's leading spaces and tabs (and a carriage return, so CRLF text reads
 * like LF text) aren't content, nor are its trailing ones; a line break inside a paragraph is a soft break.
 * Container prefixes (`>`) inside the content are skipped. Escapes and character references apply wherever
 * Lezer found them, and in the raw text of labels, titles and destinations, which Lezer leaves unparsed.
 */
import type { SyntaxNode } from '@lezer/common';
import { BACKSLASH, SPACE, TAB } from '../parser/chars.ts';
import { decodeEntity, ENTITY_SHAPE, ENTITY_WINDOW } from './entities.ts';

/**
 * How a piece of visible text is written in the source. `delimiter` is the unmatched part of a `*` or `_` run
 * whose other part is emphasis (`***a**` holds one): it stays written with its run, since escaping it would
 * shorten the run and change how the rest pairs up.
 */
export type PieceOrigin = 'literal' | 'escape' | 'entity' | 'delimiter';

/** Visible text and where it comes from: a literal run maps one to one, an escape or reference as a whole. */
export interface TextPiece {
  readonly text: string;
  readonly from: number;
  readonly to: number;
  readonly origin: PieceOrigin;
}

/** A run of text between inline constructs. Its pieces are in source order and never empty. */
export interface TextRun {
  readonly kind: 'text';
  readonly pieces: readonly TextPiece[];
}

/** A soft line break: a newline inside a paragraph. */
export interface SoftBreak {
  readonly kind: 'soft-break';
  readonly from: number;
  readonly to: number;
}

/** A hard line break: `\` or two spaces before the newline. */
export interface HardBreak {
  readonly kind: 'hard-break';
  readonly from: number;
  readonly to: number;
  /** Written as spaces: kept that way after a delimiter, whose flanking the break's first character decides. */
  readonly spaces: boolean;
}

/** A line break. */
export type LineBreak = SoftBreak | HardBreak;

/** Emphasis, strong emphasis or small caps, with its delimiters as written (`*`, `__`, `^^`). */
export interface Span {
  readonly kind: 'emphasis' | 'strong' | 'small-caps';
  readonly open: string;
  readonly close: string;
  readonly children: readonly Inline[];
  /** Its source, marks included. */
  readonly from: number;
  readonly to: number;
}

/** Where a link or image points: escapes and references applied. */
export interface LinkTarget {
  readonly destination: string;
  /** No line breaks (the part 04 log, N6); null when the source gives none. */
  readonly title: string | null;
}

/** A link or image whose target resolved, inline or through a link reference definition. */
export interface Link extends LinkTarget {
  readonly kind: 'link' | 'image';
  readonly children: readonly Inline[];
  readonly from: number;
  readonly to: number;
  /** Where its text ends: the `]`. */
  readonly textEnd: number;
}

/** `[^label]`, resolved or not (footnote-index.ts decides); the label is raw source. */
export interface FootnoteReference {
  readonly kind: 'footnote-reference';
  readonly label: string;
  readonly from: number;
  readonly to: number;
}

/**
 * A bracket pair that resolves to nothing but holds emphasis or small caps. Lezer pairs delimiters inside
 * brackets on their own, so the pair is written as brackets (serialize.ts, I6): escaped, it would let the
 * delimiters inside pair with those outside. Any other unresolved pair is plain text.
 */
export interface Brackets {
  readonly kind: 'brackets';
  /** `[` or `![`. */
  readonly open: string;
  readonly children: readonly Inline[];
  /** `]` and the label as written (`[b]`, `[]`), line breaks in it as spaces. */
  readonly close: string;
  /** The pair read as text, as the writer sees it: brackets, content and label. */
  readonly text: readonly Inline[];
  readonly from: number;
  readonly to: number;
  /** Where its content ends: the `]`. */
  readonly textEnd: number;
}

/** One inline element. */
export type Inline = TextRun | LineBreak | Span | Link | FootnoteReference | Brackets;

/** Thrown when a tree lacks a node its parent always has: the grammar changed under this module. */
export class MalformedTreeError extends Error {
  override readonly name = 'MalformedTreeError';
}

/** The `index`th child of `node` named `name` (negative counts from the end); throws if there is none. */
export function requireChild(node: SyntaxNode, name: string, index = 0): SyntaxNode {
  const found = node.getChildren(name).at(index);
  if (found === undefined) throw new MalformedTreeError(`${node.name} at ${node.from} has no ${name} (${index})`);
  return found;
}

/** Link reference definitions by normalised label: the first definition of a label wins. */
export type Definitions = ReadonlyMap<string, LinkTarget>;

/** Characters a backslash escapes (CommonMark 2.4, as Lezer's `Escape` rule reads them). */
const ESCAPABLE = '!"#$%&\'()*+,-./:;<=>?@[\\]^_`{|}~';
const NEWLINE = 10;
const CARRIAGE_RETURN = 13;
const AMPERSAND = 38;

/** Spaces, tabs and carriage returns: what a line's edges shed. */
function isLineSpace(code: number): boolean {
  return code === SPACE || code === TAB || code === CARRIAGE_RETURN;
}

/**
 * Collects the inline elements of one range. Text arrives as pieces; a newline becomes a soft break only when
 * content follows it on the next line, so the whitespace and prefixes between belong to the break.
 */
class InlineBuilder {
  private readonly out: Inline[] = [];
  private pieces: TextPiece[] = [];
  /** At a line start, spaces are skipped. */
  lineStart: boolean;
  /** Where the last content that isn't a literal space ends. */
  private contentEnd: number;
  /** Where a pending soft break starts, or null. */
  private breakFrom: number | null = null;

  constructor(from: number, lineStart: boolean) {
    this.contentEnd = from;
    this.lineStart = lineStart;
  }

  /** A literal run `from`–`to` of `source`. */
  literal(source: string, from: number, to: number): void {
    this.arrive(from);
    this.pieces.push({ text: source.slice(from, to), from, to, origin: 'literal' });
    // A run never follows a space (a line's leading ones are skipped), so the walk back stops at its start.
    let end = to;
    while (isLineSpace(source.charCodeAt(end - 1))) end -= 1;
    this.contentEnd = Math.max(this.contentEnd, end);
  }

  /** An escaped character or a character reference. */
  piece(text: string, from: number, to: number, origin: PieceOrigin): void {
    this.arrive(from);
    this.pieces.push({ text, from, to, origin });
    this.contentEnd = to;
  }

  /** An inline construct spanning `from`–`to`. */
  inline(item: Inline, from: number, to: number): void {
    this.arrive(from);
    this.flush();
    this.out.push(item);
    this.contentEnd = to;
  }

  /** A hard break; what follows starts a line. */
  hardBreak(from: number, to: number, spaces: boolean): void {
    this.inline({ kind: 'hard-break', from, to, spaces }, from, to);
    this.lineStart = true;
  }

  /** A newline in the content: the line's trailing spaces go, and a soft break waits for content. */
  newline(): void {
    this.trim();
    this.breakFrom = this.contentEnd;
    this.lineStart = true;
  }

  /**
   * The elements, once the range ends at `end`. At a block's end, trailing spaces and a pending break are
   * dropped; inside link text a pending break is kept (`[a\n](b)`).
   */
  finish(end: number, blockEnd: boolean): Inline[] {
    if (blockEnd) this.trim();
    else this.arrive(end);
    this.flush();
    return this.out;
  }

  /** Content arrives at `at`: a pending soft break ends there. */
  private arrive(at: number): void {
    this.lineStart = false;
    if (this.breakFrom === null) return;
    this.flush();
    this.out.push({ kind: 'soft-break', from: this.breakFrom, to: at });
    this.breakFrom = null;
  }

  /**
   * Drops literal spaces after the last content, from every piece they are in: a mark that reads as nothing
   * (a quote marker, an ATX heading's closing sequence) can split them into several.
   */
  private trim(): void {
    for (let last = this.pieces.at(-1); last !== undefined && last.to > this.contentEnd; last = this.pieces.at(-1)) {
      this.pieces.pop();
      if (last.from < this.contentEnd) this.pieces.push({ ...last, text: last.text.slice(0, this.contentEnd - last.from), to: this.contentEnd });
    }
  }

  /** Items read on their own (a bracket pair's content), as part of this run. */
  append(items: readonly Inline[]): void {
    for (const item of items) {
      if (item.kind === 'text') {
        for (const piece of item.pieces) this.pieces.push(piece);
      } else {
        this.flush();
        this.out.push(item);
      }
    }
  }

  private flush(): void {
    if (this.pieces.length === 0) return;
    this.out.push({ kind: 'text', pieces: this.pieces });
    this.pieces = [];
  }
}

/** The escape or character reference at `i`, before `to`, or null (Lezer's `Escape` and `Entity` rules). */
function special(source: string, i: number, to: number): TextPiece | null {
  const code = source.charCodeAt(i);
  if (code === BACKSLASH && i + 1 < to && ESCAPABLE.includes(source.charAt(i + 1))) {
    return { text: source.charAt(i + 1), from: i, to: i + 2, origin: 'escape' };
  }
  if (code !== AMPERSAND) return null;
  const shape = ENTITY_SHAPE.exec(source.slice(i + 1, Math.min(to, i + 1 + ENTITY_WINDOW)));
  if (shape === null) return null;
  const decoded = decodeEntity(`&${shape[0]}`);
  return decoded === null ? null : { text: decoded, from: i, to: i + 1 + shape[0].length, origin: 'entity' };
}

/** Text `from`–`to` with escapes and references applied, and no other markup: literal runs between them. */
export function rawPieces(source: string, from: number, to: number): TextPiece[] {
  const out: TextPiece[] = [];
  let run = from;
  for (let i = from; i < to; i += 1) {
    const found = special(source, i, to);
    if (found === null) continue;
    if (i > run) out.push({ text: source.slice(run, i), from: run, to: i, origin: 'literal' });
    out.push(found);
    i = found.to - 1;
    run = found.to;
  }
  if (to > run) out.push({ text: source.slice(run, to), from: run, to, origin: 'literal' });
  return out;
}

/** What one stored text run reads as: escapes and character references applied (the inverse of escaping). */
export function unescapeText(raw: string): string {
  return rawPieces(raw, 0, raw.length)
    .map((piece) => piece.text)
    .join('');
}

/**
 * Raw text `from`–`to` into the builder: escapes and character references apply, newlines break lines, and
 * a line's leading spaces are skipped.
 */
function scan(source: string, from: number, to: number, builder: InlineBuilder): void {
  let run = from;
  const flush = (end: number): void => {
    if (end > run) builder.literal(source, run, end);
  };
  for (let i = from; i < to; i += 1) {
    const code = source.charCodeAt(i);
    if (code === NEWLINE) {
      flush(i);
      builder.newline();
      run = i + 1;
      continue;
    }
    if (builder.lineStart && isLineSpace(code)) {
      run = i + 1;
      continue;
    }
    // Only a backslash or an ampersand starts an escape or a reference (decision 61: this runs on every character of a
    // chapter each keystroke, in the preview's layout).
    const found = code === BACKSLASH || code === AMPERSAND ? special(source, i, to) : null;
    if (found === null) {
      builder.lineStart = false;
      continue;
    }
    flush(i);
    builder.piece(found.text, found.from, found.to, found.origin);
    i = found.to - 1;
    run = found.to;
  }
  flush(to);
}

/** What reading inline content needs besides the tree: the source and the chapter's link definitions. */
export interface InlineContext {
  readonly source: string;
  readonly definitions: Definitions;
}

/**
 * The inline elements of `node` between `from` and `to`: text in the gaps between its children, and each
 * child that lies inside the range. `block` says the range is a block's content, which starts and ends a line
 * (link text, titles and labels do neither).
 */
export function readInline(node: SyntaxNode, from: number, to: number, cx: InlineContext, block = false): Inline[] {
  const builder = new InlineBuilder(from, block);
  walk(node, from, to, builder, cx);
  return builder.finish(to, block);
}

/** The delimiter character code an emphasis node opens and closes with, or NaN (which matches nothing). */
function emphasisCharacter(node: SyntaxNode | null, source: string): number {
  return node?.name === 'Emphasis' || node?.name === 'StrongEmphasis' ? source.charCodeAt(node.from) : Number.NaN;
}

/**
 * The children of `node` inside `from`–`to`, and the text between them. Unmatched delimiter characters
 * touching an emphasis node with the same character belong to its run and are read as `delimiter` pieces.
 * A child before the range is a mark ending at `from` (a link's or span's opening), which reads as nothing;
 * the first child past the range ends it, since children come in order.
 */
function walk(node: SyntaxNode, from: number, to: number, builder: InlineBuilder, cx: InlineContext): void {
  let pos = from;
  let previous: SyntaxNode | null = null;
  for (let child = node.firstChild; child !== null && child.to <= to; child = child.nextSibling) {
    gap(cx.source, pos, child.from, builder, emphasisCharacter(previous, cx.source), emphasisCharacter(child, cx.source));
    element(child, builder, cx);
    pos = child.to;
    previous = child;
  }
  gap(cx.source, pos, to, builder, emphasisCharacter(previous, cx.source), Number.NaN);
}

/** How many characters with code `code` open `text` (`step` 1) or close it (`step` -1). */
function runLength(text: string, code: number, step: 1 | -1): number {
  let n = 0;
  while (text.charCodeAt(step === 1 ? n : text.length - 1 - n) === code) n += 1;
  return n;
}

/**
 * The text between two children: `after` is the delimiter character of the emphasis node just before it,
 * `before` that of the one just after (NaN for none), so the unmatched part of their runs is marked.
 */
function gap(source: string, from: number, to: number, builder: InlineBuilder, after: number, before: number): void {
  const text = source.slice(from, to);
  const lead = runLength(text, after, 1);
  const tail = Math.min(runLength(text, before, -1), text.length - lead);
  if (lead > 0) builder.piece(source.slice(from, from + lead), from, from + lead, 'delimiter');
  scan(source, from + lead, to - tail, builder);
  if (tail > 0) builder.piece(source.slice(to - tail, to), to - tail, to, 'delimiter');
}

/** One inline node. Anything not named here is a container prefix (`QuoteMark`) and is skipped. */
function element(node: SyntaxNode, builder: InlineBuilder, cx: InlineContext): void {
  const { from, to } = node;
  switch (node.name) {
    case 'Escape':
      builder.piece(cx.source.charAt(from + 1), from, to, 'escape');
      return;
    case 'Entity': {
      const decoded = decodeEntity(cx.source.slice(from, to));
      if (decoded === null) builder.literal(cx.source, from, to);
      else builder.piece(decoded, from, to, 'entity');
      return;
    }
    case 'HardBreak':
      builder.hardBreak(from, to, cx.source.charCodeAt(from) !== BACKSLASH);
      return;
    case 'Emphasis':
      span(node, 'emphasis', 'EmphasisMark', builder, cx);
      return;
    case 'StrongEmphasis':
      span(node, 'strong', 'EmphasisMark', builder, cx);
      return;
    case 'SmallCaps':
      span(node, 'small-caps', 'SmallCapsMark', builder, cx);
      return;
    case 'Link':
    case 'Image':
      link(node, builder, cx);
      return;
    case 'FootnoteReference': {
      const label = requireChild(node, 'FootnoteLabel');
      builder.inline({ kind: 'footnote-reference', label: cx.source.slice(label.from, label.to), from, to }, from, to);
      return;
    }
    default:
  }
}

function span(node: SyntaxNode, kind: Span['kind'], mark: string, builder: InlineBuilder, cx: InlineContext): void {
  const open = requireChild(node, mark);
  const close = requireChild(node, mark, 1);
  const children = readInline(node, open.to, close.from, cx);
  const item: Span = { kind, open: cx.source.slice(open.from, open.to), close: cx.source.slice(close.from, close.to), children, from: node.from, to: node.to };
  builder.inline(item, node.from, node.to);
}

/** Whitespace in a link label (CommonMark 2.1), line tabulation aside: that one is replaced first. */
const LABEL_SPACE = /[ \t\n\f\r]+/gu;
const LINE_TABULATION = '\u000b';

/** A link label as matched: whitespace collapsed and trimmed, case folded (CommonMark 4.7); "" is no label. */
export function labelKey(raw: string): string {
  return raw.replaceAll(LINE_TABULATION, ' ').replace(LABEL_SPACE, ' ').replace(/^ | $/gu, '').toLowerCase().toUpperCase();
}

/** Every container prefix (`QuoteMark`) inside `node`, in order. */
function quoteMarks(node: SyntaxNode, out: SyntaxNode[]): SyntaxNode[] {
  for (let child = node.firstChild; child !== null; child = child.nextSibling) {
    if (child.name === 'QuoteMark') out.push(child);
    else quoteMarks(child, out);
  }
  return out;
}

/**
 * The source `from`–`to` without the container prefixes inside `node`, all of which must lie in the range: a
 * label, or the text of a reference link (which has no destination or title for a prefix to sit beside).
 */
export function rawText(node: SyntaxNode, from: number, to: number, source: string): string {
  const parts: string[] = [];
  let pos = from;
  for (const mark of quoteMarks(node, [])) {
    parts.push(source.slice(pos, mark.from));
    pos = mark.to;
  }
  parts.push(source.slice(pos, to));
  return parts.join('');
}

/** The text of a range as one string: escapes and references applied, line breaks as spaces. */
export function plainText(node: SyntaxNode, from: number, to: number, cx: InlineContext): string {
  const parts: string[] = [];
  for (const item of readInline(node, from, to, cx)) {
    if (item.kind === 'text') for (const piece of item.pieces) parts.push(piece.text);
    else parts.push(' ');
  }
  return parts.join('');
}

/** A `LinkTitle`'s text: the quotes stripped, and every line break a space (N6). */
export function titleText(title: SyntaxNode, cx: InlineContext): string {
  return plainText(title, title.from + 1, title.to - 1, cx).replace(/[\n\r]/gu, ' ');
}

/** A `URL`'s destination: angle brackets stripped, escapes and references applied. */
export function destinationText(url: SyntaxNode, cx: InlineContext): string {
  const angle = cx.source.charAt(url.from) === '<';
  return plainText(url, url.from + (angle ? 1 : 0), url.to - (angle ? 1 : 0), cx);
}

/**
 * A `Link` or `Image`. An inline target (`(…)`) always resolves; a reference (`[a][b]`, `[a][]`, `[a]`)
 * resolves when a definition has its label. One that doesn't is text: its brackets and label as written,
 * its inner content kept (part 02 log, notes).
 */
function link(node: SyntaxNode, builder: InlineBuilder, cx: InlineContext): void {
  const open = requireChild(node, 'LinkMark');
  const close = requireChild(node, 'LinkMark', 1);
  const inline = node.getChildren('LinkMark').length > 2;
  const label = node.getChild('LinkLabel');
  let target: LinkTarget | null;
  if (inline) {
    const url = node.getChild('URL');
    const title = node.getChild('LinkTitle');
    target = {
      destination: url === null ? '' : destinationText(url, cx),
      title: title === null ? null : titleText(title, cx),
    };
  } else {
    const labelRaw = label === null ? '' : rawText(label, label.from + 1, label.to - 1, cx.source);
    const key = labelKey(labelRaw === '' ? rawText(node, open.to, close.from, cx.source) : labelRaw);
    target = cx.definitions.get(key) ?? null;
  }
  if (target !== null) {
    const children = readInline(node, open.to, close.from, cx);
    const kind = node.name === 'Image' ? 'image' : 'link';
    builder.inline({ kind, ...target, children, from: node.from, to: node.to, textEnd: close.from }, node.from, node.to);
    return;
  }
  const children = readInline(node, open.to, close.from, cx);
  if (!holdsDelimiter(children)) {
    // Plain text in the run around it, so escapes that look along a run (E19) see all of it. The content is read
    // once: nested pairs (`![![![a]]]`) would otherwise be read again at every level, exponentially.
    builder.literal(cx.source, open.from, open.to);
    builder.append(children);
    builder.literal(cx.source, close.from, close.to);
    if (label !== null) walk(label, label.from, label.to, builder, cx);
    return;
  }
  // The pair as text shares its content's objects, so a span N11 writes plain is plain here too.
  const text: Inline[] = [
    literalRun(cx.source, open.from, open.to),
    ...children,
    literalRun(cx.source, close.from, close.to),
    ...(label === null ? [] : readInline(label, label.from, label.to, cx)),
  ];
  const labelSource = label === null ? '' : rawText(label, label.from, label.to, cx.source).replace(/[ \t]*\n[ \t]*/gu, ' ');
  const item: Brackets = {
    kind: 'brackets',
    open: cx.source.slice(open.from, open.to),
    children,
    close: `]${labelSource}`,
    text,
    from: node.from,
    to: node.to,
    textEnd: close.from,
  };
  builder.inline(item, node.from, node.to);
}

/** Source `from`–`to` as one literal text run. */
function literalRun(source: string, from: number, to: number): TextRun {
  return { kind: 'text', pieces: [{ text: source.slice(from, to), from, to, origin: 'literal' }] };
}

/**
 * Whether content holds emphasis or small caps outside any link, image or bracket pair of its own: whatever
 * Lezer paired inside the brackets without seeing the delimiters outside them. A delimiter piece counts
 * through its span, which it always touches. Spans in `plain` are written without marks (serialize.ts, N11)
 * and don't count, though what they hold does.
 */
export function holdsDelimiter(content: readonly Inline[], plain: ReadonlySet<Inline> = new Set()): boolean {
  return content.some(
    (item) => (item.kind === 'emphasis' || item.kind === 'strong' || item.kind === 'small-caps') && (!plain.has(item) || holdsDelimiter(item.children, plain)),
  );
}
